import { useEffect, useRef, useState } from "react";
import { Check, Pencil, X } from "lucide-react";
import { cn } from "@/lib/utils";

type Kind = "text" | "email" | "tel" | "url" | "date" | "datetime" | "number" | "longtext";

/**
 * Click-to-edit primitive used across the contact-detail left rail.
 *
 * Display mode: shows the value (or a faded placeholder) plus a hover
 * pencil affordance. Click anywhere on the row to switch into edit mode.
 *
 * Edit mode: focused input. `Enter` saves (Shift+Enter inserts newline in
 * longtext). `Esc` cancels. Blur saves only if the value changed — clicking
 * the explicit ✕ button cancels without saving regardless.
 *
 * `onSave` is called with the trimmed string value (or `null` when empty).
 * If it throws, the row reverts to the previous value and shows the error.
 */
export function InlineEditField({
  value, onSave, kind = "text", placeholder, label, className, formatDisplay,
}: {
  value: string | null | undefined;
  onSave: (next: string | null) => Promise<void> | void;
  kind?: Kind;
  placeholder?: string;
  label?: string;
  className?: string;
  formatDisplay?: (v: string) => React.ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing) {
      setDraft(value ?? "");
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [editing, value]);

  const commit = async () => {
    const next = draft.trim();
    if (next === (value ?? "")) { setEditing(false); return; }
    setBusy(true);
    try {
      await onSave(next === "" ? null : next);
      setEditing(false);
    } catch {
      // Caller's onSave should toast — we just stay in edit mode so the
      // user can retry or cancel.
    } finally { setBusy(false); }
  };
  const cancel = () => { setDraft(value ?? ""); setEditing(false); };

  if (editing) {
    const onKey = (e: React.KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); cancel(); return; }
      if (e.key === "Enter" && !(kind === "longtext" && e.shiftKey)) {
        e.preventDefault();
        void commit();
      }
    };
    const inputCls = "flex-1 rounded-md border border-indigo-500/40 bg-zinc-900 px-2 py-1 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-indigo-500 focus:outline-none";
    return (
      <div className={cn("flex items-start gap-1", className)}>
        {kind === "longtext" ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={onKey}
            placeholder={placeholder}
            rows={3}
            className={cn(inputCls, "resize-none")}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type={kind === "text" ? "text" : kind === "datetime" ? "datetime-local" : kind}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={onKey}
            placeholder={placeholder}
            className={inputCls}
          />
        )}
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); void commit(); }}
          disabled={busy}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-emerald-400 hover:bg-zinc-800"
          aria-label="Save"
        >
          <Check className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); cancel(); }}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
          aria-label="Cancel"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  const isEmpty = value == null || value === "";
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title={label ? `Edit ${label.toLowerCase()}` : "Edit"}
      className={cn(
        "group flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 -mx-1.5 text-left transition-colors hover:bg-zinc-800/40",
        className,
      )}
    >
      <span className={cn("flex-1 truncate text-sm", isEmpty ? "italic text-zinc-600" : "text-zinc-200")}>
        {isEmpty
          ? (placeholder ?? `Add ${(label ?? "").toLowerCase() || "value"}…`)
          : (formatDisplay && value ? formatDisplay(value) : value)}
      </span>
      <Pencil className="h-3 w-3 shrink-0 text-zinc-600 opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );
}

/**
 * Click-to-edit dropdown. Same shape as InlineEditField but renders a
 * <select> in edit mode. The value list is provided by the caller.
 */
export function InlineEditSelect({
  value, options, onSave, label, className,
}: {
  value: string | null | undefined;
  options: { value: string; label: string }[];
  onSave: (next: string) => Promise<void> | void;
  label?: string;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? options[0]?.value ?? "");
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (editing) {
      setDraft(value ?? options[0]?.value ?? "");
      setTimeout(() => ref.current?.focus(), 0);
    }
  }, [editing, value, options]);

  const commit = async (next: string) => {
    if (next === (value ?? "")) { setEditing(false); return; }
    setBusy(true);
    try { await onSave(next); setEditing(false); }
    catch { /* caller toasts */ }
    finally { setBusy(false); }
  };

  const current = options.find((o) => o.value === value);
  if (editing) {
    return (
      <select
        ref={ref}
        value={draft}
        onChange={(e) => { setDraft(e.target.value); void commit(e.target.value); }}
        onBlur={() => setEditing(false)}
        disabled={busy}
        className={cn("w-full rounded-md border border-indigo-500/40 bg-zinc-900 px-2 py-1 text-sm text-zinc-100 focus:border-indigo-500 focus:outline-none", className)}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title={label ? `Change ${label.toLowerCase()}` : "Change"}
      className={cn(
        "group flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 -mx-1.5 text-left transition-colors hover:bg-zinc-800/40",
        className,
      )}
    >
      <span className={cn("flex-1 truncate text-sm capitalize", value ? "text-zinc-200" : "italic text-zinc-600")}>
        {current?.label ?? value ?? `Set ${(label ?? "").toLowerCase()}…`}
      </span>
      <Pencil className="h-3 w-3 shrink-0 text-zinc-600 opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );
}

/**
 * Inline yes/no toggle that flips on click. Matches the look of the
 * surrounding edit fields.
 */
export function InlineEditCheckbox({
  value, onSave, label, danger,
}: {
  value: boolean;
  onSave: (next: boolean) => Promise<void> | void;
  label: string;
  danger?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try { await onSave(!value); } catch { /* caller toasts */ }
    finally { setBusy(false); }
  };
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 -mx-1.5 hover:bg-zinc-800/40">
      <input
        type="checkbox"
        checked={value}
        disabled={busy}
        onChange={toggle}
        className={cn(
          "h-3.5 w-3.5 rounded border-zinc-700 bg-zinc-900",
          danger ? "text-rose-500" : "text-indigo-500",
        )}
      />
      <span className={cn("text-xs", danger ? "text-rose-300" : "text-zinc-300")}>{label}</span>
    </label>
  );
}
